import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../utils/api';
import {
  MessageSquare, Save, Send, Settings, FileText, ToggleLeft, ToggleRight,
  CheckCircle, XCircle, Loader2, AlertTriangle, RefreshCw,
} from 'lucide-react';
import { type SupportedPhoneCountry } from '@digitaltattoo/shared-utils';
import { InternationalPhoneInput } from '../components/InternationalPhoneInput';

interface SmsSettings {
  smsEnabled: boolean;
  smsSenderId: string | null;
  smsReminderD7: boolean;
  smsReminderD1: boolean;
  smsReminderH2: boolean;
  hasCredentials: boolean;
}

interface SmsTemplate {
  id: string;
  name: string;
  body: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export function SmsSettingsPage() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<SmsSettings | null>(null);
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testPhoneCountry, setTestPhoneCountry] = useState<SupportedPhoneCountry>('FR');
  const [testPhoneE164, setTestPhoneE164] = useState<string | null>(null);
  const [testPhoneIsValid, setTestPhoneIsValid] = useState(false);
  const [testMessage, setTestMessage] = useState('Ceci est un SMS de test depuis Digital Tattoo.');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [saveResult, setSaveResult] = useState<{ success: boolean; message: string } | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');
  const [activeTab, setActiveTab] = useState<'settings' | 'templates'>('settings');

  useEffect(() => {
    loadSettings();
    loadTemplates();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await fetch(getApiUrl('/api/admin/sms-settings'), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (data.success) setSettings(data.data);
    } catch (err) {
      console.error('Failed to load SMS settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const res = await fetch(getApiUrl('/api/admin/sms-templates'), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (data.success) setTemplates(data.data);
    } catch (err) {
      console.error('Failed to load SMS templates:', err);
    }
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    setSaveResult(null);
    try {
      const res = await fetch(getApiUrl('/api/admin/sms-settings'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          smsEnabled: settings.smsEnabled,
          smsSenderId: settings.smsSenderId,
          smsReminderD7: settings.smsReminderD7,
          smsReminderD1: settings.smsReminderD1,
          smsReminderH2: settings.smsReminderH2,
        }),
      });
      const data = await res.json();
      setSaveResult({
        success: data.success,
        message: data.success ? 'Paramètres sauvegardés' : 'Erreur lors de la sauvegarde',
      });
      if (data.success) setTimeout(() => setSaveResult(null), 3000);
    } catch (err) {
      setSaveResult({ success: false, message: 'Erreur réseau' });
    } finally {
      setSaving(false);
    }
  };

  const sendTestSms = async () => {
    if (!testMessage) return;
    if (!testPhoneIsValid || !testPhoneE164) {
      setTestResult({ success: false, message: 'Le numéro de téléphone est invalide pour le pays sélectionné.' });
      return;
    }
    setTestSending(true);
    setTestResult(null);
    try {
      const res = await fetch(getApiUrl('/api/notifications/test-sms'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ phone: testPhoneE164, message: testMessage }),
      });
      const data = await res.json();
      setTestResult({ success: data.success, message: data.message });
      if (data.success) setTimeout(() => setTestResult(null), 5000);
    } catch (err) {
      setTestResult({ success: false, message: 'Erreur réseau' });
    } finally {
      setTestSending(false);
    }
  };

  const saveTemplate = async (templateId: string) => {
    try {
      const res = await fetch(getApiUrl(`/api/admin/sms-templates/${templateId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ body: editBody }),
      });
      const data = await res.json();
      if (data.success) {
        setTemplates(templates.map((t) => (t.id === templateId ? { ...t, body: editBody } : t)));
        setEditingTemplate(null);
      }
    } catch (err) {
      console.error('Failed to save template:', err);
    }
  };

  const toggleTemplateActive = async (templateId: string, isActive: boolean) => {
    try {
      const res = await fetch(getApiUrl(`/api/admin/sms-templates/${templateId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ isActive: !isActive }),
      });
      const data = await res.json();
      if (data.success) {
        setTemplates(templates.map((t) => (t.id === templateId ? { ...t, isActive: !isActive } : t)));
      }
    } catch (err) {
      console.error('Failed to toggle template:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-7 h-7 text-primary" />
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary">Paramètres SMS</h1>
            <p className="text-sm text-textSecondary">Configuration TextMagic et modèles SMS</p>
          </div>
        </div>
      </div>

      {/* Credential warning */}
      {settings && !settings.hasCredentials && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-amber-800">Identifiants TextMagic non configurés</p>
            <p className="text-sm text-amber-700 mt-1">
              Les variables <code className="bg-amber-100 px-1 rounded">TEXTMAGIC_USERNAME</code> et{' '}
              <code className="bg-amber-100 px-1 rounded">TEXTMAGIC_API_KEY</code> ne sont pas définies dans le fichier .env.
              Les SMS seront affichés dans la console uniquement (mode développement).
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          <button
            onClick={() => setActiveTab('settings')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'settings'
                ? 'border-primary text-primary'
                : 'border-transparent text-textSecondary hover:text-textPrimary'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-1.5" />
            Configuration
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'templates'
                ? 'border-primary text-primary'
                : 'border-transparent text-textSecondary hover:text-textPrimary'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-1.5" />
            Modèles SMS ({templates.length})
          </button>
        </nav>
      </div>

      {activeTab === 'settings' && settings && (
        <div className="space-y-6">
          {/* SMS Enable/Disable */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-textPrimary mb-4">Activation</h2>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-textPrimary">Activer les SMS</p>
                <p className="text-sm text-textSecondary">Active l'envoi de SMS via TextMagic pour les notifications</p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, smsEnabled: !settings.smsEnabled })}
                className="flex items-center gap-2"
              >
                {settings.smsEnabled ? (
                  <ToggleRight className="w-10 h-10 text-green-500" />
                ) : (
                  <ToggleLeft className="w-10 h-10 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {/* Sender ID */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-textPrimary mb-4">Identifiant expéditeur</h2>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">Sender ID (optionnel)</label>
              <input
                type="text"
                value={settings.smsSenderId || ''}
                onChange={(e) => setSettings({ ...settings, smsSenderId: e.target.value.slice(0, 11) || null })}
                placeholder="DigitalTat"
                maxLength={11}
                className="input-field w-64"
              />
              <p className="text-xs text-textSecondary mt-1">
                Nom affiché comme expéditeur (max 11 caractères alphanumériques). Laissez vide pour utiliser le numéro par défaut.
              </p>
            </div>
          </div>

          {/* Reminder Settings */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-textPrimary mb-4">Rappels automatiques</h2>
            <div className="space-y-4">
              {[
                { key: 'smsReminderD7' as const, label: 'Rappel J-7', desc: '7 jours avant le rendez-vous' },
                { key: 'smsReminderD1' as const, label: 'Rappel J-1', desc: '1 jour avant le rendez-vous' },
                { key: 'smsReminderH2' as const, label: 'Rappel H-2', desc: '2 heures avant le rendez-vous' },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium text-textPrimary">{label}</p>
                    <p className="text-sm text-textSecondary">{desc}</p>
                  </div>
                  <button onClick={() => setSettings({ ...settings, [key]: !settings[key] })}>
                    {settings[key] ? (
                      <ToggleRight className="w-8 h-8 text-green-500" />
                    ) : (
                      <ToggleLeft className="w-8 h-8 text-gray-400" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Save */}
          <div className="flex items-center gap-4">
            <button onClick={saveSettings} disabled={saving} className="btn-primary flex items-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Sauvegarder
            </button>
            {saveResult && (
              <span className={`text-sm flex items-center gap-1 ${saveResult.success ? 'text-green-600' : 'text-red-600'}`}>
                {saveResult.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                {saveResult.message}
              </span>
            )}
          </div>

          {/* Test SMS */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-textPrimary mb-4">Envoyer un SMS de test</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-1">Numéro de téléphone</label>
                <InternationalPhoneInput
                  value={testPhone}
                  country={testPhoneCountry}
                  required
                  onChange={(result) => {
                    setTestPhone(result.displayValue);
                    setTestPhoneCountry(result.country);
                    setTestPhoneE164(result.e164Value);
                    setTestPhoneIsValid(result.isValid);
                    if (result.isValid) setTestResult(null);
                  }}
                  selectClassName="input-field w-full mb-2"
                  inputClassName="input-field w-full"
                  placeholder="+33612345678"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-1">Message</label>
                <textarea
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  rows={2}
                  className="input-field w-full"
                />
              </div>
            </div>
            <div className="flex items-center gap-4 mt-4">
              <button
                onClick={sendTestSms}
                disabled={testSending || !testPhone}
                className="btn-secondary flex items-center gap-2"
              >
                {testSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Envoyer le test
              </button>
              {testResult && (
                <span className={`text-sm flex items-center gap-1 ${testResult.success ? 'text-green-600' : 'text-red-600'}`}>
                  {testResult.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  {testResult.message}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'templates' && (
        <div className="space-y-4">
          <p className="text-sm text-textSecondary">
            Modifiez les modèles SMS utilisés pour les notifications. Placeholders disponibles:{' '}
            <code className="bg-gray-100 px-1 rounded text-xs">{'{{clientFirstName}}'}</code>,{' '}
            <code className="bg-gray-100 px-1 rounded text-xs">{'{{appointmentDate}}'}</code>,{' '}
            <code className="bg-gray-100 px-1 rounded text-xs">{'{{appointmentTime}}'}</code>,{' '}
            <code className="bg-gray-100 px-1 rounded text-xs">{'{{serviceName}}'}</code>,{' '}
            <code className="bg-gray-100 px-1 rounded text-xs">{'{{shopName}}'}</code>,{' '}
            <code className="bg-gray-100 px-1 rounded text-xs">{'{{shopPhone}}'}</code>,{' '}
            <code className="bg-gray-100 px-1 rounded text-xs">{'{{otpCode}}'}</code>
          </p>

          {templates.map((tpl) => (
            <div key={tpl.id} className="bg-white rounded-lg border border-gray-200 p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-textPrimary">{tpl.name}</h3>
                  <p className="text-xs text-textSecondary font-mono">{tpl.id}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleTemplateActive(tpl.id, tpl.isActive)}
                    className={`text-xs px-2 py-1 rounded ${
                      tpl.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {tpl.isActive ? 'Actif' : 'Inactif'}
                  </button>
                </div>
              </div>

              {editingTemplate === tpl.id ? (
                <div className="space-y-3">
                  <textarea
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)}
                    rows={3}
                    className="input-field w-full font-mono text-sm"
                  />
                  <div className="text-xs text-textSecondary">
                    {editBody.length} caractères · ~{Math.ceil(editBody.length / 160)} segment(s)
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => saveTemplate(tpl.id)} className="btn-primary text-sm py-1.5 px-3">
                      Sauvegarder
                    </button>
                    <button onClick={() => setEditingTemplate(null)} className="btn-secondary text-sm py-1.5 px-3">
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-textPrimary bg-gray-50 rounded p-3 font-mono">{tpl.body}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-textSecondary">
                      {tpl.body.length} car. · ~{Math.ceil(tpl.body.length / 160)} segment(s)
                    </span>
                    <button
                      onClick={() => {
                        setEditingTemplate(tpl.id);
                        setEditBody(tpl.body);
                      }}
                      className="text-sm text-primary hover:text-primary/80"
                    >
                      Modifier
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
